import { FC, Fragment, useEffect, useState } from "react";
import { BoxProps } from "@mui/material";

import { useAlarmPaletteResolver } from "../../../hooks/use-alarm-color";
import { useScreenSize } from "../../../utils/screen";
import { useGauge } from "./use-gauge";
import { Wrapper } from "./styled";

export interface IGaugeChart extends BoxProps {
  value: number;
  warningLevel: number;
  criticalLevel: number;
  arcStrokeWidth?: number;
  startAngle?: number;
  endAngle?: number;
  numTicks?: number;
  diameter?: number;
  minValue?: number;
  maxValue?: number;
  tickLength?: number;
  offset?: number;
  baseRadius?: number;
  tipRadius?: number;
  tickColor?: string;
  needleColor?: string;
}

export const GaugeChart: FC<IGaugeChart> = props => {
  const {
    value,
    warningLevel,
    criticalLevel,
    startAngle = 90,
    endAngle = 270,
    diameter = 300,
    offset = 8,
    minValue = 0,
    maxValue = 100,
    baseRadius = 12,
    tipRadius = 1,
    arcStrokeWidth = 6,
    needleColor,
    ...rest
  } = props;

  const resolveAlarmColor = useAlarmPaletteResolver();
  const { isExtraSmallScreen, isSmallScreen, isLaptopScreen, isLargeScreen, isExtraLargeScreen } = useScreenSize();
  const [valueTextOffset, setValueTextOffset] = useState(75);

  const delimiters = [
    { color: resolveAlarmColor(90), startAngle: 0, endAngle: criticalLevel - 1 },
    { color: resolveAlarmColor(60), startAngle: criticalLevel + 1, endAngle: warningLevel - 1 },
    { color: resolveAlarmColor(30), startAngle: warningLevel + 1, endAngle: 100 },
  ];

  const { ticks, getLabelProps, valueToAngle, angleToValue, getArcProps, getNeedleProps, getSVGProps } = useGauge({
    startAngle,
    endAngle,
    delimiters,
    diameter,
    domain: [minValue, maxValue],
  });

  const { tip, base, points } = getNeedleProps({
    value,
    baseRadius,
    tipRadius,
  });

  const getNeedleColor = () => {
    if (needleColor) {
      return needleColor;
    }

    if (value <= criticalLevel) {
      return resolveAlarmColor(90);
    } else if (value <= warningLevel) {
      return resolveAlarmColor(60);
    } else {
      return resolveAlarmColor(30);
    }
  };

  useEffect(() => {
    setValueTextOffset(isExtraLargeScreen ? 260 : isLargeScreen ? 195 : isLaptopScreen ? 100 : isSmallScreen ? 85 : 75);
  }, [isLaptopScreen, isSmallScreen, isLargeScreen, isExtraLargeScreen]);

  return (
    <Wrapper {...rest} sx={{ zoom: isExtraSmallScreen ? "50%" : "100%" }}>
      <svg {...getSVGProps()}>
        <defs>
          <filter x="0" y="0" width="1" height="1" id="bg">
            <feFlood floodColor="white" result="bg" />
            <feMerge>
              <feMergeNode in="bg" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path
          {...getArcProps({
            offset,
            startAngle,
            endAngle,
          })}
          fill="none"
          strokeWidth={arcStrokeWidth}
          strokeLinecap="round"
        />
        {delimiters.map(({ startAngle, endAngle, color }, i) => (
          <path
            key={`${color}_${i}`}
            {...getArcProps({
              offset,
              startAngle: valueToAngle(startAngle),
              endAngle: valueToAngle(endAngle),
            })}
            fill="none"
            stroke={color}
            strokeWidth={arcStrokeWidth}
            strokeLinecap="round"
          />
        ))}
        <g id="ticks">
          {ticks.map(angle => {
            const value = angleToValue(angle);

            return (
              <Fragment key={`tick-group-${angle}`}>
                <text {...getLabelProps({ angle, offset: 20 })}>{value}</text>
              </Fragment>
            );
          })}
        </g>
        {value ? (
          <>
            <g id="needle">
              <circle fill={getNeedleColor()} {...base} r={24} />
              <circle fill={getNeedleColor()} opacity="0.9" {...base} />
              <circle fill={getNeedleColor()} opacity="0.9" {...tip} />
              <polyline fill={getNeedleColor()} points={points} />
            </g>
            <g>
              <circle {...base} fill="white" r={16} />
              <text
                {...getLabelProps({
                  angle: 90,
                  offset: valueTextOffset,
                })}
              >
                {value}
              </text>
            </g>
          </>
        ) : null}
      </svg>
    </Wrapper>
  );
};
